import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { ViewWorkshopJobStoreService } from '../../view-workshop-job-store.service';
import { DialogRef } from '@angular/cdk/dialog';
import {
  CancelledEvent,
  WorkshopJobEventDialogData,
  validateEventEdit
} from '../../../../models/workshop-job-event';
import { WORKSHOP } from '../../../../../testIds';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { MatButton } from '@angular/material/button';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { AddCurrencyPrefixDirective } from '../../../../shared/directives/add-currency-prefix.directive';
import { MatInput } from '@angular/material/input';
import { TestIdDirective } from '../../../../shared/directives/test-id.directive';
import {
  MatFormField,
  MatLabel,
  MatPrefix
} from '@angular/material/form-field';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from '@angular/material/dialog';
import { Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'cancel-dialog',
  templateUrl: './cancel-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    TestIdDirective,
    MatLabel,
    MatInput,
    MatPrefix,
    AddCurrencyPrefixDirective,
    CdkTextareaAutosize,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    AsyncPipe
  ]
})
export class CancelDialogComponent {
  testIds = WORKSHOP;
  isEditing = false;
  form = new FormGroup({
    notes: new FormControl<string>(null),
    labour_hours: new FormControl<number>(null, Validators.required),
    labour_cost: new FormControl<number>(null, Validators.required)
  });

  private currentJobStatus$ = this.store.job$.pipe(
    map((job) => job.work_status)
  );

  private validationResult$ = this.currentJobStatus$.pipe(
    map((status) => validateEventEdit('cancelled', status))
  );

  canEdit$ = this.validationResult$.pipe(map((result) => result.canEdit));
  validationMessage$ = this.validationResult$.pipe(
    map((result) => result.validationMessage)
  );

  constructor(
    private store: ViewWorkshopJobStoreService,
    private dialog: DialogRef<CancelDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: WorkshopJobEventDialogData
  ) {
    this.isEditing = !!this.data?.eventToEdit;

    if (this.isEditing) {
      this.canEdit$.pipe(take(1)).subscribe((canEdit) => {
        if (!canEdit) {
          this.form.disable();
        }
      });

      this.populateFormWithExistingEvent();
    } else {
      this.populateDefaultFromTemplate();
    }
  }

  private populateFormWithExistingEvent() {
    const event = this.data.eventToEdit as CancelledEvent;
    this.form.patchValue({
      notes: event.details.notes as string,
      labour_hours: event.details.labour_hours,
      labour_cost: event.details.labour_cost
        ? event.details.labour_cost / 100
        : null
    });
  }

  private populateDefaultFromTemplate() {
    this.store.template$
      .pipe(takeUntil(this.dialog.closed), filter(Boolean))
      .subscribe(({ labour_cost, labour_hours, parts }) => {
        if (labour_cost && this.form.value.labour_cost === null) {
          this.form.patchValue({ labour_cost: labour_cost / 100 });
        }
        if (this.form.value.labour_hours === null) {
          this.form.patchValue({ labour_hours: labour_hours });
        }
      });
  }

  saveCancellation() {
    const { notes, labour_hours, labour_cost } = this.form.value;
    const eventData: Partial<CancelledEvent> = {
      type: 'cancelled',
      details: {
        notes,
        labour_hours,
        labour_cost: labour_cost * 100
      }
    };

    if (this.isEditing) {
      const event = { ...eventData, id: this.data.eventToEdit.id };
      this.store.updateEvent({
        jobEvent: event,
        onSuccess: () => this.dialog.close()
      });
    } else {
      this.store.addEvent({
        jobEvent: eventData,
        onSuccess: () => this.dialog.close()
      });
    }
  }

  get dialogTitle(): string {
    return this.isEditing ? 'Edit Cancelled Job' : 'Cancel job';
  }

  get saveButtonText(): string {
    return this.isEditing ? 'Update' : 'Add';
  }
}
